import { http } from '@/app/lib/http/client'

export interface LoyaltyTier {
  code: 'BRONZE' | 'SILVER' | 'GOLD'
  name: string
  medal: string
  minPoints?: number
  maxPoints?: number | null
}

export interface LoyaltyConfig {
  enabled: boolean
  pointsPerPeso: number
  minimumPurchaseAmount: number
  silverMinPoints: number
  goldMinPoints: number
  tiers: LoyaltyTier[]
}

export interface Reward {
  id: number
  pointsRequired: number
  creditAmount: number
  isActive: boolean
  title: string
}

export interface LoyaltyCustomer {
  id: number
  name: string
  phone?: string | null
  email?: string | null
  avatarUrl?: string | null
  pointsBalance: number
  lifetimePoints: number
  creditBalance: number
  currentTier: LoyaltyTier
  lifetimeTier: LoyaltyTier
}

export interface LoyaltyRedemption {
  id: number
  source: 'MANUAL' | 'POS' | 'SYSTEM'
  pointsSpent: number
  creditGenerated: number
  reward: { pointsRequired: number; creditAmount: number }
  sale?: { id: number; ticketNumber: string } | null
  performedBy?: { id: number; name: string } | null
  occurredAt: string
}

export interface LoyaltyQuote {
  programEnabled: boolean
  pointsBalance: number
  lifetimePoints: number
  currentTier: LoyaltyTier
  creditBalance: number
  reward: { id: number; pointsRequired: number; creditAmount: number } | null
  maximumReward: { id: number; pointsRequired: number; creditAmount: number } | null
  nextReward: { id: number; pointsRequired: number; creditAmount: number } | null
  pointsToNextReward: number | null
  creditGenerated: number
  creditApplied: number
  creditRemaining: number
  grossTotal: number
  externalDue: number
  pointsToEarn: number
  quoteToken: string
  expiresAt: string
}

export interface PaginationResult<T> {
  data: T[]
  currentPage: number
  lastPage: number
  perPage: number
  total: number
  from?: number
  to?: number
}

const numberValue = (value: unknown) => Number.parseFloat(String(value ?? 0)) || 0
const recordValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
const unwrap = (response: { data?: { data?: unknown } }): unknown => response.data?.data

function mapTier(value: unknown): LoyaltyTier {
  const tier = recordValue(value)
  const code = ['BRONZE', 'SILVER', 'GOLD'].includes(String(tier.code))
    ? (String(tier.code) as LoyaltyTier['code'])
    : 'BRONZE'

  return {
    code,
    name: String(tier.name ?? 'Bronce'),
    medal: String(tier.medal ?? '🥉'),
    minPoints: tier.min_points === undefined ? undefined : Number(tier.min_points),
    maxPoints: tier.max_points === null ? null : tier.max_points === undefined ? undefined : Number(tier.max_points),
  }
}

function mapConfig(value: unknown): LoyaltyConfig {
  const data = recordValue(value)
  return {
    enabled: Boolean(data.enabled),
    pointsPerPeso: numberValue(data.points_per_peso),
    minimumPurchaseAmount: numberValue(data.minimum_purchase_amount),
    silverMinPoints: Number(data.silver_min_points),
    goldMinPoints: Number(data.gold_min_points),
    tiers: Array.isArray(data.tiers) ? data.tiers.map(mapTier) : [],
  }
}

function mapReward(value: unknown): Reward {
  const data = recordValue(value)
  const creditAmount = numberValue(data.credit_amount)
  return {
    id: Number(data.id),
    pointsRequired: Number(data.points_required),
    creditAmount,
    isActive: Boolean(data.is_active),
    title: `Descuento $${creditAmount.toLocaleString('es-MX')}`,
  }
}

function mapCustomer(value: unknown): LoyaltyCustomer {
  const data = recordValue(value)
  return {
    id: Number(data.id), name: String(data.name ?? ''),
    phone: data.phone == null ? null : String(data.phone), email: data.email == null ? null : String(data.email),
    avatarUrl: data.avatar_url == null ? null : String(data.avatar_url),
    pointsBalance: Number(data.points_balance || 0), lifetimePoints: Number(data.lifetime_points || 0),
    creditBalance: numberValue(data.credit_balance), currentTier: mapTier(data.current_tier), lifetimeTier: mapTier(data.lifetime_tier),
  }
}

function mapPaginator<T>(value: unknown, mapper: (item: unknown) => T): PaginationResult<T> {
  const data = recordValue(value)
  return {
    data: Array.isArray(data.data) ? data.data.map(mapper) : [], currentPage: Number(data.current_page || 1),
    lastPage: Number(data.last_page || 1), perPage: Number(data.per_page || 12), total: Number(data.total || 0),
    from: data.from === undefined ? undefined : Number(data.from),
    to: data.to === undefined ? undefined : Number(data.to),
  }
}

function mapQuoteReward(value: unknown): LoyaltyQuote['reward'] {
  if (!value) return null
  const reward = recordValue(value)
  return {
    id: Number(reward.id),
    pointsRequired: Number(reward.points_required),
    creditAmount: numberValue(reward.credit_amount),
  }
}

export const loyaltyService = {
  async getConfig(): Promise<LoyaltyConfig> {
    return mapConfig(unwrap(await http.get('/loyalty/config')))
  },

  async updateConfig(payload: Omit<LoyaltyConfig, 'tiers'>): Promise<LoyaltyConfig> {
    return mapConfig(unwrap(await http.put('/loyalty/config', {
      enabled: payload.enabled, points_per_peso: payload.pointsPerPeso,
      minimum_purchase_amount: payload.minimumPurchaseAmount,
      silver_min_points: payload.silverMinPoints, gold_min_points: payload.goldMinPoints,
    })))
  },

  async updateStatus(enabled: boolean): Promise<LoyaltyConfig> {
    return mapConfig(unwrap(await http.patch('/loyalty/config/status', { enabled })))
  },

  async getRewards(params?: { page?: number; per_page?: number; is_active?: boolean }): Promise<PaginationResult<Reward>> {
    return mapPaginator(unwrap(await http.get('/loyalty/rewards', { params })), mapReward)
  },

  async createReward(payload: { pointsRequired: number; creditAmount: number }): Promise<Reward> {
    return mapReward(unwrap(await http.post('/loyalty/rewards', { points_required: payload.pointsRequired, credit_amount: payload.creditAmount })))
  },

  async updateReward(id: number, payload: { pointsRequired?: number; creditAmount?: number; isActive?: boolean }): Promise<Reward> {
    return mapReward(unwrap(await http.put(`/loyalty/rewards/${id}`, {
      points_required: payload.pointsRequired, credit_amount: payload.creditAmount, is_active: payload.isActive,
    })))
  },

  async updateRewardStatus(id: number, isActive: boolean): Promise<Reward> {
    return mapReward(unwrap(await http.patch(`/loyalty/rewards/${id}/status`, { is_active: isActive })))
  },

  async redeemReward(rewardId: number, customerId: number, idempotencyKey: string) {
    return unwrap(await http.post(`/loyalty/rewards/${rewardId}/redeem`, { customer_id: customerId, idempotency_key: idempotencyKey }))
  },

  async getCustomers(params?: { ranking?: 'current' | 'lifetime'; search?: string; page?: number; per_page?: number }): Promise<PaginationResult<LoyaltyCustomer>> {
    return mapPaginator(unwrap(await http.get('/loyalty/customers', { params })), mapCustomer)
  },

  async getRedemptions(customerId: number, params?: { start_date?: string; end_date?: string; page?: number; per_page?: number }): Promise<PaginationResult<LoyaltyRedemption>> {
    return mapPaginator(unwrap(await http.get(`/loyalty/customers/${customerId}/redemptions`, { params })), (value) => {
      const item = recordValue(value)
      const reward = recordValue(item.reward)
      const sale = item.sale ? recordValue(item.sale) : null
      const performedBy = item.performed_by ? recordValue(item.performed_by) : null

      return {
        id: Number(item.id), source: String(item.source) as LoyaltyRedemption['source'],
        pointsSpent: Number(item.points_spent), creditGenerated: numberValue(item.credit_generated),
        reward: { pointsRequired: Number(reward.points_required || 0), creditAmount: numberValue(reward.credit_amount) },
        sale: sale ? { id: Number(sale.id), ticketNumber: String(sale.ticket_number ?? '') } : null,
        performedBy: performedBy ? { id: Number(performedBy.id), name: String(performedBy.name ?? '') } : null,
        occurredAt: String(item.occurred_at ?? ''),
      }
    })
  },

  async quote(payload: { customerId: number; useLoyalty: boolean; isDelivery: boolean }): Promise<LoyaltyQuote> {
    const data = recordValue(unwrap(await http.post('/pos/loyalty/quote', {
      customer_id: payload.customerId, use_loyalty: payload.useLoyalty, is_delivery: payload.isDelivery,
    })))
    return {
      programEnabled: Boolean(data.program_enabled), pointsBalance: Number(data.points_balance || 0),
      lifetimePoints: Number(data.lifetime_points || 0), currentTier: mapTier(data.current_tier),
      creditBalance: numberValue(data.credit_balance), reward: mapQuoteReward(data.reward),
      maximumReward: mapQuoteReward(data.maximum_reward),
      nextReward: mapQuoteReward(data.next_reward),
      pointsToNextReward: data.points_to_next_reward == null ? null : Number(data.points_to_next_reward),
      creditGenerated: numberValue(data.credit_generated), creditApplied: numberValue(data.credit_applied),
      creditRemaining: numberValue(data.credit_remaining), grossTotal: numberValue(data.gross_total),
      externalDue: numberValue(data.external_due), pointsToEarn: Number(data.points_to_earn || 0),
      quoteToken: String(data.quote_token ?? ''), expiresAt: String(data.expires_at ?? ''),
    }
  },
}
